import React, { useState } from 'react';
import { CheckCircle2, ArrowRight, ShieldCheck, Sparkles } from 'lucide-react';
import { ClarvosCTAButton, ClarvosPillBadge, AbilityBrandLogo } from './ClarvosBrand';

export const RequestDemoPage: React.FC<{ onBackToHome: () => void }> = ({ onBackToHome }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    institution: '',
    role: 'Therapist / Clinician',
    need: 'AAC & Assistive Communication',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.email) {
      setSubmitted(true);
    }
  };

  return (
    <div className="bg-clarvos-black text-white min-h-screen pt-28 pb-24 px-6 sm:px-12 flex flex-col justify-center items-center relative overflow-hidden">
      
      {/* Background radial glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-clarvos-yellow/5 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-4xl w-full mx-auto text-center relative z-10">
        
        <ClarvosPillBadge label="Schedule Walkthrough" variant="yellow" className="mb-6" />

        <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight mb-4 max-w-3xl mx-auto leading-tight">
          Specialized Care Doesn't Have To Be Complex
        </h1>

        <p className="text-lg sm:text-xl text-clarvos-gray-300 max-w-2xl mx-auto leading-relaxed mb-12">
          Find the clarity, telemetry, and assistive empowerment your team needs with AbilityOS.
        </p>

        {submitted ? (
          <div className="bg-clarvos-yellow text-clarvos-black p-12 sm:p-16 rounded-[40px] shadow-2xl max-w-xl mx-auto text-center animate-fade-in">
            <div className="w-16 h-16 rounded-full bg-clarvos-black text-white mx-auto flex items-center justify-center mb-6">
              <CheckCircle2 className="w-8 h-8 text-clarvos-yellow" />
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight mb-4">
              Walkthrough Confirmed
            </h2>
            <p className="text-base text-clarvos-charcoal leading-relaxed mb-8">
              Thank you, <span className="font-bold">{formData.firstName || 'Colleague'}</span>. Our clinical engineering team has received your request for <span className="font-bold">{formData.institution || 'your organization'}</span> and will reach out to <span className="underline font-bold">{formData.email}</span> within 4 business hours.
            </p>
            <ClarvosCTAButton
              text="Return to Platform Overview"
              hoverText="Explore AbilityOS"
              onClick={onBackToHome}
              variant="dark"
              size="lg"
            />
          </div>
        ) : (
          /* Clarvos Signature Solar Yellow Centered Form Card */
          <div className="bg-clarvos-yellow text-clarvos-black p-8 sm:p-14 rounded-[40px] shadow-2xl max-w-xl mx-auto text-left relative">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-clarvos-black">
                    First Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Jane"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full bg-white/90 border border-black/10 rounded-full px-5 py-3.5 text-sm text-clarvos-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-clarvos-black transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-clarvos-black">
                    Last Name
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full bg-white/90 border border-black/10 rounded-full px-5 py-3.5 text-sm text-clarvos-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-clarvos-black transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-clarvos-black">
                  Work / Institution Email
                </label>
                <input
                  type="email"
                  required
                  placeholder="jane.doe@schoolclinic.org"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-white/90 border border-black/10 rounded-full px-5 py-3.5 text-sm text-clarvos-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-clarvos-black transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-clarvos-black">
                  School District or Clinic Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="Metro Public Schools / Neuro-Rehab Institute"
                  value={formData.institution}
                  onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                  className="w-full bg-white/90 border border-black/10 rounded-full px-5 py-3.5 text-sm text-clarvos-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-clarvos-black transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-clarvos-black">
                    Your Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full bg-white/90 border border-black/10 rounded-full px-5 py-3.5 text-sm text-clarvos-black focus:outline-none focus:ring-2 focus:ring-clarvos-black transition-all"
                  >
                    <option>Therapist / SLP / OT</option>
                    <option>Special Ed Director</option>
                    <option>School Administrator</option>
                    <option>Clinical Neurologist</option>
                    <option>Family / Caregiver</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2 text-clarvos-black">
                    Primary Interest
                  </label>
                  <select
                    value={formData.need}
                    onChange={(e) => setFormData({ ...formData, need: e.target.value })}
                    className="w-full bg-white/90 border border-black/10 rounded-full px-5 py-3.5 text-sm text-clarvos-black focus:outline-none focus:ring-2 focus:ring-clarvos-black transition-all"
                  >
                    <option>AAC & Speech Assistive</option>
                    <option>Motor Access & CP Dwell</option>
                    <option>Neuro-Rehab Metronome</option>
                    <option>Complete District Suite</option>
                  </select>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  className="w-full bg-clarvos-black text-white hover:bg-clarvos-charcoal py-4 rounded-full font-extrabold text-base flex items-center justify-center gap-3 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                >
                  <span>Submit Demo Request</span>
                  <div className="w-7 h-7 rounded-full bg-clarvos-yellow text-clarvos-black flex items-center justify-center">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              </div>

              <div className="flex items-center justify-center gap-2 text-[11px] font-bold text-clarvos-charcoal text-center pt-2">
                <ShieldCheck className="w-4 h-4 text-emerald-800" />
                <span>Zero-spam guarantee • HIPAA & FERPA Compliant</span>
              </div>

            </form>
          </div>
        )}

      </div>
    </div>
  );
};
